/**
 * jayesstudio-pdp-gallery-a11y.js (JSR-149)
 *
 * Progressive-enhancement a11y fixes for Horizon's core PDP media-gallery
 * slideshow. PDP-scoped, additive, no layout/behavior change. Lives in an
 * asset (not a core edit) because Horizon's core slideshow.js continuously
 * re-writes both `aria-selected` (on dots) and `aria-hidden` (on slides), so
 * the fix must run alongside that JS and survive `npm run install-core`.
 *
 * Two WCAG defects fixed:
 *
 * (A) DISALLOWED ARIA ON DOTS (axe `aria-allowed-attr`):
 *     Core renders each pagination dot as a plain <button> and writes
 *     `aria-selected` on it, but `aria-selected` is NOT allowed on the
 *     implicit `button` role. We set role="tablist" on the <ol> and
 *     role="tab" on each dot button. With the explicit `tab` role,
 *     `aria-selected` becomes a SUPPORTED attribute, so the value core JS
 *     keeps writing is now valid. (No fighting the runtime writes.)
 *
 * (B) ARIA-HIDDEN SUBTREE WITH FOCUSABLE CONTENT (axe `aria-hidden-focus`):
 *     Every off-screen <slideshow-slide> gets `aria-hidden="true"`, yet still
 *     contains focusable content (zoom trigger, media controls). An
 *     aria-hidden ancestor with tabbable descendants fails WCAG 4.1.2 / 2.4.3.
 *     `aria-hidden` alone does not remove descendants from the tab order;
 *     `inert` does (and hides from the a11y tree). We mirror aria-hidden ->
 *     inert via a MutationObserver so the off-screen slide's focusable
 *     descendants leave the tab order while the visible slide stays operable.
 *
 * Scope: product templates only (wired from layout/theme.liquid inside the
 * existing request.page_type == 'product' block).
 */
(function () {
  'use strict';

  /** (A) Give the dots tablist/tab roles so aria-selected becomes valid. */
  function enhanceDots(gallery) {
    var dotsList = gallery.querySelector('.slideshow-controls__dots');
    if (!dotsList || dotsList.dataset.jsA11yDots === 'done') return;
    dotsList.setAttribute('role', 'tablist');
    var dots = dotsList.querySelectorAll('button.slideshow-control');
    for (var i = 0; i < dots.length; i++) {
      dots[i].setAttribute('role', 'tab');
    }
    dotsList.dataset.jsA11yDots = 'done';
  }

  /** Mirror a slide's aria-hidden state onto the `inert` property. */
  function syncInert(slide) {
    // aria-hidden="true" -> inert; anything else (false / absent) -> not inert.
    var hidden = slide.getAttribute('aria-hidden') === 'true';
    if (slide.inert !== hidden) slide.inert = hidden;
  }

  /** (B) Observe aria-hidden on each slide and keep `inert` in sync. */
  function enhanceSlides(gallery) {
    var slides = gallery.querySelectorAll('slideshow-slide');
    for (var i = 0; i < slides.length; i++) {
      var slide = slides[i];
      if (slide.dataset.jsA11yInert === 'done') continue;
      syncInert(slide); // initial state on connect
      var observer = new MutationObserver(function (mutations) {
        for (var m = 0; m < mutations.length; m++) {
          syncInert(mutations[m].target);
        }
      });
      observer.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] });
      slide.dataset.jsA11yInert = 'done';
    }
  }

  function enhance(gallery) {
    if (!gallery) return;
    enhanceDots(gallery);
    enhanceSlides(gallery);
  }

  function init() {
    var galleries = document.querySelectorAll('media-gallery');
    for (var i = 0; i < galleries.length; i++) enhance(galleries[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run after Section Rendering API swaps (e.g. variant change re-renders
  // the gallery), so newly-injected dots/slides get enhanced too.
  document.addEventListener('shopify:section:load', init);
})();
